import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SupplierAnalytics = ({ className = '' }) => {
  const [activeTab, setActiveTab] = useState('performance');

  const supplierPerformanceData = [
    { name: 'TechWorld Supply', bestPrices: 145, totalProducts: 320, avgConfidence: 94, competitiveRate: 45.3 },
    { name: 'Global Electronics', bestPrices: 132, totalProducts: 298, avgConfidence: 91, competitiveRate: 44.3 },
    { name: 'Component Plus', bestPrices: 98, totalProducts: 245, avgConfidence: 89, competitiveRate: 40.0 },
    { name: 'Digital Depot', bestPrices: 87, totalProducts: 201, avgConfidence: 92, competitiveRate: 43.3 },
    { name: 'Circuit City Wholesale', bestPrices: 76, totalProducts: 189, avgConfidence: 88, competitiveRate: 40.2 }
  ];

  const priceDistributionData = [
    { name: '$0-$50', value: 234, color: '#10B981' },
    { name: '$50-$100', value: 189, color: '#3B82F6' },
    { name: '$100-$500', value: 156, color: '#8B5CF6' },
    { name: '$500-$1K', value: 98, color: '#F59E0B' },
    { name: '$1K+', value: 45, color: '#EF4444' }
  ];

  const matchTypeData = [
    { name: 'Exact EAN', value: 342, color: '#10B981' },
    { name: 'Exact MPN', value: 198, color: '#059669' },
    { name: 'AI Fuzzy', value: 156, color: '#F59E0B' },
    { name: 'Ambiguous', value: 26, color: '#EF4444' }
  ];

  const tabs = [
    { id: 'performance', label: 'Supplier Performance', icon: 'BarChart3' },
    { id: 'distribution', label: 'Price Distribution', icon: 'PieChart' },
    { id: 'matches', label: 'Match Types', icon: 'Target' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {entry?.dataKey}: {entry?.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Supplier Analytics</h3>
            <p className="text-sm text-muted-foreground">Performance metrics and insights</p>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Export Analytics
          </Button>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex space-x-8 px-6">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`
                flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeTab === tab?.id 
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                }
              `}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </nav>
      </div>
      {/* Content */}
      <div className="p-6">
        {activeTab === 'performance' && (
          <div>
            <div className="mb-6">
              <h4 className="text-sm font-medium text-foreground mb-4">Best Price Winners by Supplier</h4>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={supplierPerformanceData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis 
                      dataKey="name" 
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="bestPrices" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Supplier Details Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Supplier</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Best Prices</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Total Products</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Win Rate</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Avg Confidence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {supplierPerformanceData?.map((supplier, index) => (
                    <tr key={index} className="hover:bg-muted hover:bg-opacity-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                          <span className="font-medium text-foreground">{supplier?.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-semibold text-success">{supplier?.bestPrices}</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-foreground">{supplier?.totalProducts}</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-medium text-foreground">{supplier?.competitiveRate}%</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <div className="w-12 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-success transition-all duration-300"
                              style={{ width: `${supplier?.avgConfidence}%` }}
                            />
                          </div>
                          <span className="text-xs font-mono text-muted-foreground">{supplier?.avgConfidence}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'distribution' && (
          <div>
            <h4 className="text-sm font-medium text-foreground mb-4">Price Range Distribution</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={priceDistributionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {priceDistributionData?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry?.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-3">
                {priceDistributionData?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted bg-opacity-30 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: item?.color }}
                      />
                      <span className="font-medium text-foreground">{item?.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold text-foreground">{item?.value}</span>
                      <span className="text-sm text-muted-foreground ml-1">products</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'matches' && (
          <div>
            <h4 className="text-sm font-medium text-foreground mb-4">Match Type Breakdown</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={matchTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {matchTypeData?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry?.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-3">
                {matchTypeData?.map((item, index) => {
                  const total = matchTypeData?.reduce((sum, d) => sum + d?.value, 0);
                  const percentage = ((item?.value / total) * 100)?.toFixed(1);
                  
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted bg-opacity-30 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: item?.color }}
                        />
                        <span className="font-medium text-foreground">{item?.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-foreground">{item?.value}</span>
                        <span className="text-sm text-muted-foreground ml-1">({percentage}%)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierAnalytics;